import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from 'components/ui/tabs';
import { Logo } from 'components/Logo';
import { TripCard } from 'components/TripCard';
import { ParcelCard } from 'components/ParcelCard';
import { motion } from 'framer-motion';
import {
  Package, Plane, Search, ArrowRight, ShieldCheck, Star, Globe2,
  Users, MessageCircle, Sparkles, CheckCircle2,
} from 'lucide-react';
import api from 'lib/api';
import { useAuth } from 'contexts/AuthContext';
import { useLanguage } from 'lib/i18n';
import { CONTACT_EMAIL } from 'lib/contact';
import { LanguageSwitcher } from 'components/LanguageSwitcher';

const DELIVERY_IMAGES = {
  hero: '/images/delivery-hero.jpg',
  sender: '/images/parcel-packing.jpg',
  traveler: '/images/traveler-with-parcel.jpg',
  global: '/images/international-delivery.jpg',
};

const IMAGE_FALLBACK =
  'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%221400%22 height=%22800%22 viewBox=%220 0 1400 800%22%3E%3Cdefs%3E%3ClinearGradient id=%22g%22 x1=%220%22 y1=%220%22 x2=%221%22 y2=%221%22%3E%3Cstop stop-color=%22%23f59e0b%22 offset=%220%22/%3E%3Cstop stop-color=%22%23fb923c%22 offset=%220.55%22/%3E%3Cstop stop-color=%22%23fef3c7%22 offset=%221%22/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width=%221400%22 height=%22800%22 fill=%22url(%23g)%22/%3E%3Cg fill=%22%23ffffff%22 fill-opacity=%220.3%22%3E%3Ccircle cx=%22300%22 cy=%22180%22 r=%22120%22/%3E%3Ccircle cx=%221020%22 cy=%22120%22 r=%2290%22/%3E%3Ccircle cx=%221120%22 cy=%22660%22 r=%22150%22/%3E%3C/g%3E%3Ctext x=%2270%22 y=%22630%22 font-family=%22Arial%22 font-size=%2274%22 font-weight=%22700%22 fill=%22%231f2937%22%3ELivraison collaborative%3C/text%3E%3Ctext x=%2270%22 y=%22695%22 font-family=%22Arial%22 font-size=%2236%22 fill=%22%23374151%22%3EColis, trajets et confiance%3C/text%3E%3C/svg%3E';

const handleImageError = (event) => {
  event.currentTarget.onerror = null;
  event.currentTarget.src = IMAGE_FALLBACK;
};

const asArray = (value) => (Array.isArray(value) ? value : []);

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('parcel');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [featuredTrips, setFeaturedTrips] = useState([]);
  const [featuredParcels, setFeaturedParcels] = useState([]);

  useEffect(() => {
    api.get('/trips/public').then(r => setFeaturedTrips(asArray(r.data).slice(0, 4))).catch(() => setFeaturedTrips([]));
    api.get('/deliveries/public').then(r => setFeaturedParcels(asArray(r.data).slice(0, 4))).catch(() => setFeaturedParcels([]));
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (from) params.set('departure_city', from);
    if (to) params.set('arrival_city', to);
    navigate(`/search?${params.toString()}&tab=${activeTab}`);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top nav */}
      <header className="border-b bg-card/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <Link to="/" className="shrink-0"><Logo size="sm" hideTextOnMobile /></Link>
          <nav className="hidden md:flex items-center gap-1">
            <Link to="/search?tab=trip" className="px-3 py-2 text-sm font-medium hover:bg-secondary rounded-md">{t('landing.trips')}</Link>
            <Link to="/search?tab=parcel" className="px-3 py-2 text-sm font-medium hover:bg-secondary rounded-md">{t('landing.parcels')}</Link>
            <Link to="/how-it-works" className="px-3 py-2 text-sm font-medium hover:bg-secondary rounded-md">{t('landing.how')}</Link>
          </nav>
          <div className="flex shrink-0 items-center justify-end gap-1 sm:gap-2">
            <LanguageSwitcher />
            {user ? (
              <Button className="px-2 text-xs sm:px-4 sm:text-sm" onClick={() => navigate('/dashboard')} data-testid="nav-dashboard-button">{t('landing.space')}</Button>
            ) : (
              <>
                <Button className="px-2 text-xs sm:px-4 sm:text-sm" variant="ghost" onClick={() => navigate('/login')} data-testid="nav-login-button">{t('landing.login')}</Button>
                <Button className="px-2 text-xs sm:px-4 sm:text-sm" onClick={() => navigate('/register')} data-testid="nav-register-button">{t('landing.register')}</Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative hero-bg overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-12 pb-16 sm:pt-16 sm:pb-20">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="lg:col-span-7"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent text-accent-foreground text-xs font-medium mb-4">
                <Sparkles size={12} /> {t('landing.badge')}
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
                {t('landing.heroA')}<br />
                <span className="text-primary">{t('landing.heroB')}</span>
              </h1>
              <p className="text-lg text-muted-foreground mb-6 max-w-xl">
                {t('landing.description')}
              </p>

              <Card className="shadow-lg-custom">
                <CardContent className="p-4 sm:p-6">
                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="grid grid-cols-2 mb-4">
                      <TabsTrigger value="parcel" data-testid="hero-tab-parcel"><Package className="w-4 h-4 mr-1.5" /> {t('landing.sendParcel')}</TabsTrigger>
                      <TabsTrigger value="trip" data-testid="hero-tab-trip"><Plane className="w-4 h-4 mr-1.5" /> {t('landing.travel')}</TabsTrigger>
                    </TabsList>
                    <TabsContent value="parcel" className="mt-0">
                      <form onSubmit={handleSearch} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3">
                        <div>
                          <Label className="text-xs">{t('landing.departure')}</Label>
                          <Input value={from} onChange={(e) => setFrom(e.target.value)} placeholder="Paris" data-testid="landing-hero-origin-input" />
                        </div>
                        <div>
                          <Label className="text-xs">{t('landing.arrival')}</Label>
                          <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Conakry" data-testid="landing-hero-destination-input" />
                        </div>
                        <Button type="submit" className="sm:self-end h-10 px-6" data-testid="landing-hero-search-button">
                          <Search className="w-4 h-4 mr-1.5" /> {t('landing.search')}
                        </Button>
                      </form>
                    </TabsContent>
                    <TabsContent value="trip" className="mt-0">
                      <form onSubmit={handleSearch} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3">
                        <div>
                          <Label className="text-xs">{t('landing.whereFrom')}</Label>
                          <Input value={from} onChange={(e) => setFrom(e.target.value)} placeholder="Paris" data-testid="landing-hero-trip-origin-input" />
                        </div>
                        <div>
                          <Label className="text-xs">{t('landing.whereTo')}</Label>
                          <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Dakar" data-testid="landing-hero-trip-destination-input" />
                        </div>
                        <Button type="submit" className="sm:self-end h-10 px-6" data-testid="landing-hero-trip-search-button">
                          <Search className="w-4 h-4 mr-1.5" /> {t('landing.seeParcels')}
                        </Button>
                      </form>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:col-span-5 space-y-3"
            >
              <Card className="shadow-md-custom overflow-hidden border-0">
                <div className="relative h-48 sm:h-56">
                  <img
                    src={DELIVERY_IMAGES.hero}
                    alt="Livreur transportant des colis en ville"
                    loading="lazy"
                    onError={handleImageError}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="bg-card px-4 py-3">
                  <div className="text-xs uppercase tracking-wide text-primary">{t('landing.new')}</div>
                  <div className="text-base font-semibold leading-tight">{t('landing.verifiedDelivery')}</div>
                  </div>
              </Card>
              <Card className="shadow-md-custom overflow-hidden">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                      <Package size={20} />
                    </div>
                    <div>
                      <div className="font-semibold">{t('landing.publishParcel')}</div>
                      <div className="text-xs text-muted-foreground">{t('landing.fromFive')}</div>
                    </div>
                  </div>
                  <Button className="w-full" onClick={() => navigate('/register?role=sender')} data-testid="landing-hero-publish-parcel-button">
                    {t('landing.publishParcel')} <ArrowRight size={14} className="ml-1" />
                  </Button>
                </CardContent>
              </Card>
              <Card className="shadow-md-custom overflow-hidden">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                      <Plane size={20} />
                    </div>
                    <div>
                      <div className="font-semibold">{t('landing.offerTrip')}</div>
                      <div className="text-xs text-muted-foreground">{t('landing.makeTripPay')}</div>
                    </div>
                  </div>
                  <Button variant="outline" className="w-full" onClick={() => navigate('/register?role=traveler')} data-testid="landing-hero-publish-trip-button">
                    {t('nav.publishTrip')} <ArrowRight size={14} className="ml-1" />
                  </Button>
                </CardContent>
              </Card>
              <div className="grid grid-cols-3 gap-2 pt-1">
                <Stat number="10k+" label={t('landing.users')} />
                <Stat number="50+" label={t('landing.countries')} />
                <Stat number="4.9" label={t('landing.averageRating')} icon={Star} />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-y bg-card">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <TrustItem icon={ShieldCheck} label={t('landing.verifiedProfiles')} />
            <TrustItem icon={Globe2} label={t('landing.international')} />
            <TrustItem icon={MessageCircle} label={t('landing.messaging')} />
            <TrustItem icon={CheckCircle2} label={t('landing.liveTracking')} />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold mb-2">{t('landing.howTitle')}</h2>
            <p className="text-muted-foreground">{t('landing.simpleSecure')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <Step n="1" icon={Package} title={t('landing.stepPublish')} desc={t('landing.stepPublishDesc')} />
            <Step n="2" icon={Users} title={t('landing.stepConnect')} desc={t('landing.stepConnectDesc')} />
            <Step n="3" icon={CheckCircle2} title={t('landing.stepDeliver')} desc={t('landing.stepDeliverDesc')} />
          </div>
        </div>
      </section>

      {/* Featured */}
      {(featuredTrips.length > 0 || featuredParcels.length > 0) && (
        <section className="py-14 bg-secondary/40">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <Tabs defaultValue="trips">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">{t('landing.discover')}</h2>
                <TabsList>
                  <TabsTrigger value="trips">{t('landing.recentTrips')}</TabsTrigger>
                  <TabsTrigger value="parcels">{t('landing.recentParcels')}</TabsTrigger>
                </TabsList>
              </div>
              <TabsContent value="trips">
                <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-4">
                  {featuredTrips.map(t => <TripCard key={t.id} trip={t} to={user ? `/trips/${t.id}` : '/login'} />)}
                  {featuredTrips.length === 0 && <div className="text-sm text-muted-foreground">{t('landing.noTrips')}</div>}
                </div>
              </TabsContent>
              <TabsContent value="parcels">
                <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-4">
                  {featuredParcels.map(p => <ParcelCard key={p.id} parcel={p} to={user ? `/deliveries/${p.id}` : '/login'} />)}
                  {featuredParcels.length === 0 && <div className="text-sm text-muted-foreground">{t('landing.noParcels')}</div>}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </section>
      )}

      {/* Visual inspiration */}
      <section className="py-14 bg-card border-y">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-1">{t('landing.trustTitle')}</h2>
              <p className="text-muted-foreground">{t('landing.trustDescription')}</p>
            </div>
            <Button variant="outline" onClick={() => navigate('/search?tab=trip')} data-testid="visual-strip-search-button">
              {t('landing.exploreTrips')} <ArrowRight size={14} className="ml-1" />
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <VisualCard
              image={DELIVERY_IMAGES.sender}
              alt={t('landing.carefulPreparation')}
              title={t('landing.carefulPreparation')}
              subtitle={t('landing.carefulDescription')}
            />
            <VisualCard
              image={DELIVERY_IMAGES.traveler}
              alt={t('landing.availableTravelers')}
              title={t('landing.availableTravelers')}
              subtitle={t('landing.availableDescription')}
            />
            <VisualCard
              image={DELIVERY_IMAGES.global}
              alt={t('landing.globalNetwork')}
              title={t('landing.globalNetwork')}
              subtitle={t('landing.globalDescription')}
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl font-bold mb-3">{t('landing.ready')}</h2>
          <p className="text-muted-foreground mb-6">{t('landing.joinToday')}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" onClick={() => navigate('/register?role=sender')} data-testid="cta-publish-parcel-button">
              {t('landing.publishParcel')}
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate('/register?role=traveler')} data-testid="cta-publish-trip-button">
              {t('landing.offerTrip')}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-card">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid sm:grid-cols-2 md:grid-cols-4 gap-6 text-sm">
          <div>
            <Logo size="sm" />
            <p className="mt-3 text-muted-foreground">{t('landing.tagline')}</p>
          </div>
          <div>
            <div className="font-semibold mb-2">{t('landing.service')}</div>
            <ul className="space-y-1 text-muted-foreground">
              <li><Link to="/search?tab=trip">{t('landing.trips')}</Link></li>
              <li><Link to="/search?tab=parcel">{t('landing.parcels')}</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-semibold mb-2">{t('landing.legal')}</div>
            <ul className="space-y-1 text-muted-foreground">
              <li>{t('landing.terms')}</li>
              <li>{t('landing.privacy')}</li>
              <li>{t('landing.cookies')}</li>
            </ul>
          </div>
          <div>
            <div className="font-semibold mb-2">Support</div>
            <ul className="space-y-1 text-muted-foreground">
              <li>{t('landing.faq')}</li>
              <li><a className="hover:text-primary hover:underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t py-4 text-center text-xs text-muted-foreground">
          © 2026 ColiBuddy. {t('landing.rights')}
        </div>
      </footer>
    </div>
  );
}

const Stat = ({ number, label, icon: Icon }) => (
  <div className="text-center">
    <div className="text-2xl font-bold flex items-center justify-center gap-1">
      {number} {Icon && <Icon size={16} className="fill-amber-500 text-amber-500" />}
    </div>
    <div className="text-xs text-muted-foreground">{label}</div>
  </div>
);

const TrustItem = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2">
    <Icon size={18} className="text-primary" />
    <span className="font-medium">{label}</span>
  </div>
);

const Step = ({ n, icon: Icon, title, desc }) => (
  <Card className="text-center">
    <CardContent className="p-6">
      <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-3">
        <Icon size={22} />
      </div>
      <div className="text-xs text-primary font-semibold mb-1">ÉTAPE {n}</div>
      <h3 className="font-semibold text-lg mb-1">{title}</h3>
      <p className="text-sm text-muted-foreground">{desc}</p>
    </CardContent>
  </Card>
);

const VisualCard = ({ image, alt, title, subtitle }) => (
  <Card className="overflow-hidden shadow-md-custom border-0">
    <div className="relative h-56">
      <img src={image} alt={alt} loading="lazy" onError={handleImageError} className="h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
        <h3 className="font-semibold text-lg mb-1">{title}</h3>
        <p className="text-sm text-white/90">{subtitle}</p>
      </div>
    </div>
  </Card>
);
